/**
 * Browser-direct blob upload plumbing — the two primitives BOTH upload
 * surfaces share (client documents, 0131; card attachments, 0162).
 *
 * The three-step SAS handshake is the same on both: the server hands
 * back a single-use write URL, the browser PUTs the bytes STRAIGHT to
 * storage (step 2 never touches a handler), and the complete call
 * records the digest. Only the routes and the policy differ.
 *
 * Extracted here (card-attachments design §6.3) rather than copied: a
 * second divergent sha/PUT implementation is exactly how two surfaces
 * that must behave identically drift apart.
 */

import { ApiError } from './api';

/**
 * Handshake step 2: the browser PUTs the file bytes straight to blob
 * storage using the single-use write URL. No body echo on failure — a
 * storage error page is engine noise, not copy.
 */
export async function putFileBytes(
  uploadUrl: string,
  file: Blob,
  contentType: string,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-blob-content-type': contentType,
    },
    body: file,
  });
  if (!res.ok) {
    throw new ApiError(res.status, null, `upload PUT reached HTTP ${res.status}`);
  }
}

/** Lowercase-hex sha256 of the file bytes (recorded at complete). */
export async function sha256HexOf(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
